'use client'

/**
 * UI component
 * should be removed to a shared common repository
 */

import React from 'react'

import colors from 'tailwindcss/colors'

export type SelectInput2Props = {
  /**
   * id of the input useful for e2e testing ;)
   */
  id?: string
  /**
   * Name of the input field
   */
  name: string
  /**
   * Column span for the grid layout
   */
  colSpan?: string
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, the component is displayed in an error state.
   * @default false
   */
  error?: boolean
  /**
   * Helper text to display below the input
   */
  helperText?: string
  /**
   * If `true`, the input will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean
  /**
   * Label for the input
   */
  label?: React.ReactNode
  /**
   * If `true`, the label is displayed as required and the input element is required.
   * @default false
   */
  required?: boolean
  /**
   * If `true`, the input is read-only
   * @default false
   */
  readOnly?: boolean
  /**
   * The value of the select element
   */
  value: string[] | string
  /**
   * Callback fired when the value changes
   */
  onChange: (event: React.ChangeEvent<{ value: unknown }>) => void
  /**
   * Options for the select element
   */
  options: { value: string; name: string }[]
  /**
   * If `true`, allows multiple selections
   * @default false
   */
  multiple?: boolean
  /**
   * Default class for the input element
   */
  defaultInputClass?: string
  /**
   * Default class for the label element
   */
  defaultLabelClass?: string
}

export const SelectInput2 = React.forwardRef(function SelectInput2(
  props: SelectInput2Props,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const {
    colSpan = 'col-span-full',
    id,
    name,
    label,
    options,
    readOnly,
    disabled,
    required,
    error,
    helperText,
    value,
    onChange,
    multiple,
    defaultInputClass = 'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 h-[41px] pl-2',
    defaultLabelClass = 'block text-sm font-medium leading-6 text-gray-900',
    ...rest
  } = props

  const inputClasses = `${defaultInputClass} ${error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`

  return (
    <div className={colSpan}>
      {label && (
        <label htmlFor={id || name} className={defaultLabelClass}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <select
        id={id || name}
        name={name}
        className={inputClasses}
        value={value}
        onChange={onChange}
        disabled={disabled || readOnly}
        required={required}
        multiple={multiple}
        ref={ref as React.Ref<HTMLSelectElement>}
        aria-invalid={error ? true : undefined}
        aria-describedby={helperText ? `${id || name}-helper` : undefined}
        {...rest}
      >
        {options.map((option, key) => (
          <option key={key} value={option.value}>
            {option.name}
          </option>
        ))}
      </select>
      {helperText && (
        <p id={`${id || name}-helper`} role={error ? 'alert' : undefined} className={`mt-1 text-sm ${error ? 'text-red-500' : 'text-gray-500'}`}>
          {helperText}
        </p>
      )}
    </div>
  )
})

SelectInput2.displayName = 'SelectInput2'

export default SelectInput2
